import { useOpenAiGlobal } from "../use-openai-global";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type Bed = {
  type?: string;
  size?: string;
  count?: number;
};

type Room = {
  roomId?: string;
  name: string;
  picture?: string;
  bookUrl?: string;
  price?: number;
  currency?: string;
  typeStay?: string;
  teamIds?: string[];
  summary?: string[];
  totalRoomsAvailable?: number;
  maxOccupancy?: number;
  beds?: Bed[];
  roomType?: string;
  roomCategory?: string;
  surfaceArea?: string;
  qualifier?: string;
};

type RoomsCarouselPayload = {
  widget?: string;
  view?: string;
  rooms?: Room[];
  totalRooms?: number;
  searchParams?: {
    checkIn: string;
    nights: number;
    rooms: number;
    adults: number;
    children?: number;
    babies?: number;
    promoCode?: string;
    teamIDs?: string[];
  } | null;
  error?: string;
  errorMessage?: string;
};

function useContainerCardWidth(
  containerRef: React.RefObject<HTMLDivElement | null>,
) {
  const [cardWidth, setCardWidth] = useState(440);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      if (w < 480) setCardWidth(w - 24);
      else if (w < 768) setCardWidth(380);
      else setCardWidth(440);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef]);
  return cardWidth;
}

const HOTEL_NAME_LOOKUP: Record<string, string> = {
  "3414": "Alfred Sommier Paris",
  "2776": "Arcade Paris",
  "4133": "Barnett Paris",
  "516": "Bassano Paris",
  "11": "Baume Hotel Paris",
  "2775": "Bedford Paris",
  "935": "Bel Ami Paris",
  "67": "Belle Juliette Hotel and Spa Paris",
  "1425": "Berry Antony",
  "71": "Campanile Paris Bercy Village",
  "468": "Cler Hotel Paris",
  "82": "Dames du Pantheon",
  "83": "Design Sorbonne",
  "2427": "Dream Castle Disneyland Paris",
  "2721": "Du Bois Paris",
  "934": "Edouard 7 Paris",
  "2720": "Elysees Ceramic Paris",
  "2617": "Elysees Union Paris",
  "3280": "Esprit Saint Germain Paris",
  "506": "Explorers Disneyland Paris",
  "2193": "Fauchon Paris",
  "802": "France Quartier Latin",
  "269": "Grand Hotel Dore",
  "268": "Grand Hotel Francais",
  "2426": "Grand Magic Hotel Disneyland Paris",
  "911": "Grand Pigalle Paris",
  "3272": "Grands Boulevards Paris",
  "3695": "H4 Hotel Wyndham Paris Pleyel Resort",
  "933": "Hotel De Sers Paris",
  "4431": "Hotel des Mines Paris",
  "1428": "Hotels Paris Rive Gauche",
  "3395": "Jungle Paris CDG Airport",
  "2713": "Kouhana Hotels",
  "3817": "La Demeure Montaigne Paris",
  "26": "Les Grands Hommes",
  "146": "Lumen Paris Louvre",
  "2506": "Mama Shelter Paris East",
  "2515": "Mama Shelter Paris La Defense",
  "2505": "Mama Shelter Paris West",
  "489": "Mareuil Paris",
  "86": "Marignan Champs-Elysees",
  "3244": "Moliere Paris",
  "937": "Montalembert Paris",
  "2618": "Montfleuri Paris",
  "270": "Observatoire Luxembourg Paris",
  "520": "Ouest Hotel Paris",
  "263": "Printemps Paris Nation",
  "4208": "Pulitzer Paris",
  "3115": "Pullman Paris Bercy",
  "2030": "Relais des Halles Paris",
  "1574": "Relais du Louvre Paris",
  "3691": "Relais Madeleine Paris",
  "3690": "Relais Montmartre Paris",
  "3689": "Relais Saint-Honore Paris",
  "2648": "Residence Charles Floquet Paris",
  "126": "Sacha Paris",
  "4209": "Salvia Paris",
  "73": "Six Hotel Paris",
  "1920": "St Andre des Arts Paris",
  "46": "St Petersbourg Paris",
  "931": "Vernet Paris",
  "7": "Villathena",
  "3186": "Warwick Paris",
  "3215": "Westminster Paris",
  "4618": "Lord Byron Paris",
};

const getHotelLabel = (teamIds?: string[]) => {
  if (!teamIds || teamIds.length === 0) return "Paris Hotel";
  const [first] = teamIds;
  return HOTEL_NAME_LOOKUP[first] ?? `Paris Hotel`;
};

const isRoomsPayload = (
  payload: RoomsCarouselPayload | null,
): payload is RoomsCarouselPayload => {
  if (!payload) return false;
  if (payload.view === "rooms") return true;
  if (payload.widget === "hotel_carousel") return true;
  return Array.isArray(payload.rooms);
};

export default function HotelCarousel() {
  const toolOutput = useOpenAiGlobal(
    "toolOutput",
  ) as RoomsCarouselPayload | null;

  const [rooms, setRooms] = useState<Room[]>([]);
  const [searchParams, setSearchParams] =
    useState<RoomsCarouselPayload["searchParams"]>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardWidth = useContainerCardWidth(containerRef);
  const cardWidthWithGap = cardWidth + 24;

  useEffect(() => {
    if (!toolOutput) return;

    if (toolOutput.error || toolOutput.errorMessage) {
      setRooms([]);
      setSearchParams(null);
      setIsLoading(false);
      return;
    }

    if (isRoomsPayload(toolOutput)) {
      setRooms(toolOutput.rooms ?? []);
      setSearchParams(toolOutput.searchParams ?? null);
      setIsLoading(false);
    }
  }, [toolOutput]);

  // Reset scroll index whenever new rooms arrive
  useEffect(() => {
    setCurrentIndex(0);
    const container = containerRef.current;
    if (container) {
      container.scrollTo({ left: 0 });
    }
  }, [rooms]);

  // Track scroll position for pagination dots
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // If scrolled to the end, snap to last index
      const atEnd =
        container.scrollLeft + container.clientWidth >=
        container.scrollWidth - 2;
      if (atEnd) {
        setCurrentIndex(Math.max(rooms.length - 1, 0));
        return;
      }
      const newIndex = Math.round(container.scrollLeft / cardWidthWithGap);
      const bounded = Math.max(
        0,
        Math.min(newIndex, Math.max(rooms.length - 1, 0)),
      );
      setCurrentIndex(bounded);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [rooms.length, cardWidthWithGap]);

  const scrollToIndex = useCallback(
    (index: number) => {
      const container = containerRef.current;
      if (!container) return;
      container.scrollTo({
        left: index * cardWidthWithGap,
        behavior: "smooth",
      });
    },
    [cardWidthWithGap],
  );

  const getPriceInfo = (room: Room) => {
    const nightsRaw =
      searchParams?.nights ?? toolOutput?.searchParams?.nights ?? 1;
    const nights = Math.max(1, Number(nightsRaw) || 1);
    const formatPrice = (value: number) => Math.round(value).toLocaleString();
    const rawPrice =
      (room as any).pricePerNight ??
      room.price ??
      (room as any).totalPrice ??
      (room as any).price_per_night ??
      (room as any).total_price;
    const priceNumber = Number(rawPrice);
    const isStay = room.typeStay?.toLowerCase() === "stay";

    if (!Number.isFinite(priceNumber) || priceNumber <= 0) {
      return { displayPrice: "—", unitLabel: "per night" };
    }

    if (isStay && nights > 0) {
      const perNight = priceNumber / nights;
      return { displayPrice: formatPrice(perNight), unitLabel: "per night" };
    }

    return { displayPrice: formatPrice(priceNumber), unitLabel: "per night" };
  };

  const formatBeds = (beds?: Bed[]) => {
    if (!beds || beds.length === 0) return null;
    return beds
      .map((bed) => {
        const countPrefix = bed.count && bed.count > 0 ? `${bed.count}× ` : "";
        const sizeSuffix = bed.size ? ` (${bed.size})` : "";
        return `${countPrefix}${bed.type ?? "Bed"}${sizeSuffix}`;
      })
      .join(", ");
  };

  const capitalizeFirst = (value?: string) => {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const hasError = useMemo(() => {
    return Boolean(toolOutput?.error || toolOutput?.errorMessage);
  }, [toolOutput]);

  const errorMessage = useMemo(() => {
    if (!toolOutput) return "";
    return toolOutput.errorMessage ?? toolOutput.error ?? "";
  }, [toolOutput]);

  const headerSubtitle = useMemo(() => {
    if (rooms.length > 0) {
      return `${rooms.length} room${rooms.length === 1 ? "" : "s"} available`;
    }
    if (isLoading) {
      return "Searching for available rooms";
    }
    return "Ask for dates to see availability";
  }, [rooms.length, isLoading]);

  const searchSummary = useMemo(() => {
    if (!searchParams) return "";
    const parts: string[] = [];
    parts.push(`Check-in on ${searchParams.checkIn}`);
    parts.push(
      `${searchParams.nights} night${searchParams.nights > 1 ? "s" : ""}`,
    );
    parts.push(
      `${searchParams.rooms} room${searchParams.rooms > 1 ? "s" : ""}`,
    );
    parts.push(
      `${searchParams.adults} adult${searchParams.adults > 1 ? "s" : ""}`,
    );
    if (searchParams.children && searchParams.children > 0) {
      parts.push(
        `${searchParams.children} child${
          searchParams.children > 1 ? "ren" : ""
        }`,
      );
    }
    if (searchParams.babies && searchParams.babies > 0) {
      parts.push(
        `${searchParams.babies} infant${searchParams.babies > 1 ? "s" : ""}`,
      );
    }
    return parts.join(" · ");
  }, [searchParams]);

  const renderStatusState = (
    emoji: string,
    title: string,
    message: string,
    action?: () => void,
  ) => (
    <div className="min-h-screen bg-white rounded-2xl overflow-hidden border border-black/10 flex items-center justify-center p-4 sm:p-8">
      <div className="text-center bg-white border-2 border-black rounded-2xl shadow-xl flex flex-col items-center gap-4 sm:gap-6 p-6 sm:p-12 max-w-md w-full">
        <div className="w-20 h-20 rounded-full bg-black flex items-center justify-center">
          <span className="text-4xl text-white">{emoji}</span>
        </div>
        <div>
          <h3 className="font-bold text-black text-lg mb-2">{title}</h3>
          <p className="text-gray-600 text-sm">{message}</p>
        </div>
        {action && (
          <button
            onClick={action}
            className="px-5 py-3 border-2 border-black text-black font-semibold rounded-xl hover:bg-black hover:text-white transition-colors"
          >
            Try again
          </button>
        )}
      </div>
    </div>
  );

  if (hasError) {
    return renderStatusState(
      "⚠️",
      "Unable to load rooms",
      errorMessage || "Something went wrong while fetching rooms.",
      () => {
        window.openai?.sendFollowUpMessage?.({
          prompt: "Check room availability",
        });
      },
    );
  }

  if (isLoading) {
    return renderStatusState(
      "⏳",
      "Fetching rooms",
      "Hang tight while we look for available rooms.",
    );
  }

  if (!rooms.length) {
    return renderStatusState(
      "🛏️",
      "No rooms available",
      "Ask for different dates or hotels to refresh availability.",
    );
  }

  return (
    <div className="w-full bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-black/10 dark:border-white/10">
      <header className="relative z-10 bg-black text-white border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <h1 className="text-lg sm:text-2xl font-black tracking-tight">
              Available Rooms
            </h1>
            <span className="text-[10px] sm:text-xs text-white/50 font-medium tracking-wide whitespace-nowrap flex items-center gap-1">
              Powered by
              <button
                onClick={() =>
                  window.openai?.openExternal?.({
                    href: "https://www.quinta.im/",
                  })
                }
                className="text-white font-bold hover:underline cursor-pointer"
              >
                Quinta
              </button>
            </span>
          </div>
          <p className="text-white text-sm sm:text-base font-semibold mt-1">
            {headerSubtitle}
          </p>
          {searchSummary && (
            <p className="text-white text-xs sm:text-sm mt-1 sm:mt-2 uppercase tracking-wide">
              {searchSummary}
            </p>
          )}
          <p className="text-white/60 text-[10px] sm:text-xs mt-2">
            Prices and availability may change. Final confirmation occurs on the
            booking provider.
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-8 py-6 sm:py-12 dark:text-white">
        <div className="relative">
          <button
            className="hidden sm:flex absolute -left-1 sm:left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/80 backdrop-blur-md border border-gray-600 text-white hover:bg-black hover:shadow-lg transition-all duration-300 items-center justify-center disabled:opacity-40"
            onClick={() => scrollToIndex(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
            aria-label="Previous room"
          >
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <button
            className="hidden sm:flex absolute -right-1 sm:right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/80 backdrop-blur-md border border-gray-600 text-white hover:bg-black hover:shadow-lg transition-all duration-300 items-center justify-center disabled:opacity-40"
            onClick={() =>
              scrollToIndex(Math.min(rooms.length - 1, currentIndex + 1))
            }
            disabled={currentIndex === rooms.length - 1}
            aria-label="Next room"
          >
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          <div
            ref={containerRef}
            className="flex gap-4 sm:gap-6 overflow-x-auto scroll-smooth scrollbar-hide pb-4 sm:pb-6 snap-x snap-mandatory sm:snap-none"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {rooms.map((room, index) => {
              const priceInfo = getPriceInfo(room);
              const bedsText = formatBeds(room.beds);
              const occupancyText =
                room.maxOccupancy && room.maxOccupancy > 0
                  ? `${room.maxOccupancy} guest${
                      room.maxOccupancy > 1 ? "s" : ""
                    }`
                  : null;
              const availabilityText =
                room.totalRoomsAvailable != null && room.totalRoomsAvailable > 0
                  ? `${room.totalRoomsAvailable} room${room.totalRoomsAvailable > 1 ? "s" : ""} left`
                  : null;

              return (
                <article
                  key={room.roomId ?? `${room.name}-${index}`}
                  className="flex-none group snap-center"
                  style={{ width: cardWidth }}
                >
                  <div className="bg-white rounded-2xl overflow-hidden border-2 border-black hover:shadow-2xl transition-all duration-300 flex flex-col h-full">
                    <div className="relative h-36 sm:h-48 overflow-hidden bg-black flex-shrink-0">
                      {room.picture ? (
                        <img
                          src={room.picture}
                          alt={room.name}
                          className="w-full h-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-white text-2xl mb-2">🏨</div>
                            {room.name && (
                              <span className="text-white text-sm font-medium px-3">
                                {room.name}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="p-4 sm:p-5 flex flex-col flex-grow">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide mb-2 uppercase">
                        {getHotelLabel(room.teamIds)}
                      </p>
                      <div className="flex items-start justify-between gap-2 sm:gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          {room.name && (
                            <h2 className="text-black text-base sm:text-xl font-bold line-clamp-3 leading-tight">
                              {room.name}
                            </h2>
                          )}
                          {/* Detailed room information - commented out to simplify display */}
                          {/* <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-700 dark:text-gray-300">
                            {room.roomCategory && (
                              <span className="px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
                                {room.roomCategory}
                              </span>
                            )}
                            {room.roomType && (
                              <span className="px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
                                {room.roomType}
                              </span>
                            )}
                            {room.qualifier && (
                              <span className="px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
                                {capitalizeFirst(room.qualifier)}
                              </span>
                            )}
                            {occupancyText && (
                              <span className="px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
                                {occupancyText}
                              </span>
                            )}
                            {bedsText && (
                              <span className="px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
                                {bedsText}
                              </span>
                            )}
                            {room.surfaceArea && (
                              <span className="px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
                                {room.surfaceArea}
                              </span>
                            )}
                            {availabilityText && (
                              <span className="px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/40 border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-300">
                                {availabilityText}
                              </span>
                            )}
                          </div> */}
                        </div>
                        {(room.price ?? room.currency) && (
                          <div className="text-right flex-shrink-0">
                            <div className="text-black text-base sm:text-xl font-bold leading-tight">
                              {priceInfo.displayPrice} {room.currency ?? ""}
                            </div>
                            <div className="text-gray-600 text-xs">
                              {priceInfo.unitLabel}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Room summary/description - commented out to simplify display */}
                      {/* {room.summary && room.summary.length > 0 && (
                        <div className="mb-3 max-h-60 overflow-y-auto pr-1">
                          <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300 list-disc list-inside">
                            {room.summary.map((line, lineIndex) => (
                              <li key={lineIndex} className="leading-snug">
                                {line}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )} */}

                      <button
                        className="w-full bg-black text-white font-semibold py-2.5 sm:py-3 text-sm sm:text-base rounded-xl transition-all duration-300 hover:bg-gray-800 mt-auto disabled:opacity-50"
                        onClick={() => {
                          if (room.bookUrl) {
                            window.openai?.openExternal?.({
                              href: room.bookUrl,
                            });
                          }
                        }}
                        disabled={!room.bookUrl}
                      >
                        {room.bookUrl ? "Book now" : "Contact for availability"}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="flex items-center justify-center gap-3 mt-6 sm:mt-8">
            <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
              {currentIndex + 1} / {rooms.length}
            </span>
            <div
              className="relative w-24 sm:w-32 h-1.5 bg-gray-200 dark:bg-white rounded-full overflow-hidden cursor-pointer"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const ratio = (e.clientX - rect.left) / rect.width;
                scrollToIndex(Math.round(ratio * (rooms.length - 1)));
              }}
            >
              <div
                className="absolute inset-y-0 left-0 bg-black dark:bg-black rounded-full transition-all duration-300"
                style={{
                  width: `${((currentIndex + 1) / rooms.length) * 100}%`,
                }}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
